import { useState, useEffect } from 'react';

export interface ScoreEntry {
  id: string;
  examName: string;
  domain: string;
  examType: 'math' | 'reading-writing' | 'mock';
  score: number;
  total: number;
  percentage: number;
  timestamp: number;
  subtopicScores?: {
    name: string;
    score: number;
    total: number;
  }[];
}

const STORAGE_KEY = 'sat_score_history';

export const useScoreHistory = () => {
  const [history, setHistory] = useState<ScoreEntry[]>([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setHistory(parsed.sort((a: ScoreEntry, b: ScoreEntry) => b.timestamp - a.timestamp));
      }
    } catch (error) {
      console.error('Failed to load score history:', error);
    }
  };

  const saveScore = (entry: Omit<ScoreEntry, 'id' | 'timestamp'>) => {
    try {
      const newEntry: ScoreEntry = {
        ...entry,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
      };

      const stored = localStorage.getItem(STORAGE_KEY);
      const existing = stored ? JSON.parse(stored) : [];
      const updated = [newEntry, ...existing];

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setHistory(updated);
    } catch (error) {
      console.error('Failed to save score:', error);
    }
  };

  const clearHistory = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHistory([]);
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  };

  const getHistoryByExam = (examName: string) => {
    return history.filter(entry => entry.examName === examName);
  };

  const getHistoryByDomain = (domain: string) => {
    return history.filter(entry => entry.domain === domain);
  };

  const getHistoryByType = (examType: 'math' | 'reading-writing' | 'mock') => {
    return history.filter(entry => entry.examType === examType);
  };

  return {
    history,
    saveScore,
    clearHistory,
    getHistoryByExam,
    getHistoryByDomain,
    getHistoryByType,
  };
};